import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Users, Calendar, UserCircle, LogOut, ChevronRight, Trophy, Home } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soccer-pro-21/artifacts/6hs5jyaw_image-removebg-preview.png";

const ManagerLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const [team, setTeam] = useState(null);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const { data } = await axios.get(`${API}/manager/my-team`, { withCredentials: true });
        setTeam(data);
      } catch (error) {
        console.error("Error fetching team:", error);
      }
    };
    if (user?.role === "team_manager") fetchTeam();
  }, [user]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="manager-layout">
      {/* Header */}
      <header className="bg-[#09090b] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/manager" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="ABFA" className="h-10" />
              <div>
                <p className="font-heading font-bold uppercase text-sm">Team Manager</p>
                {team && <p className="text-xs text-[#FFC72C]">{team.name}</p>}
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/manager" className="text-sm font-medium uppercase tracking-wider hover:text-[#FFC72C] transition-colors" data-testid="manager-nav-dashboard">
                Dashboard
              </Link>
              <Link to="/manager/players" className="text-sm font-medium uppercase tracking-wider hover:text-[#FFC72C] transition-colors" data-testid="manager-nav-players">
                Roster
              </Link>
              <Link to="/manager/matches" className="text-sm font-medium uppercase tracking-wider hover:text-[#FFC72C] transition-colors" data-testid="manager-nav-matches">
                Matches
              </Link>
              <Link to="/" className="text-sm font-medium uppercase tracking-wider hover:text-[#FFC72C] transition-colors flex items-center gap-1">
                <Home className="w-4 h-4" /> Site
              </Link>
            </nav>

            <div className="flex items-center gap-3">
              <div className="hidden md:block text-right">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-gray-400">{user?.email}</p>
              </div>
              <button
                onClick={logout}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                title="Logout"
                data-testid="manager-logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Mobile Nav */}
          <div className="md:hidden flex overflow-x-auto gap-4 pb-3 -mt-1">
            <Link to="/manager" className="text-xs uppercase tracking-wider whitespace-nowrap hover:text-[#FFC72C]">Dashboard</Link>
            <Link to="/manager/players" className="text-xs uppercase tracking-wider whitespace-nowrap hover:text-[#FFC72C]">Roster</Link>
            <Link to="/manager/matches" className="text-xs uppercase tracking-wider whitespace-nowrap hover:text-[#FFC72C]">Matches</Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {title && (
          <div className="mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase text-[#09090b]" data-testid="manager-page-title">
              {title}
            </h1>
            {team && <p className="text-gray-500 mt-1">Managing {team.name}</p>}
          </div>
        )}
        {children}
      </main>
    </div>
  );
};

export default ManagerLayout;
